/**
 * The register as a browser: kind, then model, then each airframe, with what
 * it is made of, every drone id it has held, and every change to the record.
 *
 * Nothing here deletes history. Removing a part marks it removed and keeps
 * it, and removing an airframe that has ever connected retires it; the server
 * says which happened and this screen repeats it rather than assuming.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  catalogueAirframe,
  fitComponent,
  getAirframe,
  getInventory,
  getPlatforms,
  INVENTORY_STATUSES,
  removeAirframe,
  removeComponent,
  savePlatformSpec,
  updateAirframe,
  type AirframeDetail,
  type IdentifyInput,
  type InventoryItem,
  type InventoryStatus,
  type Platform,
  type PlatformSpecInput,
} from '@/api/inventory';
import { useHasRole } from '@/stores/authStore';

import {
  Chip,
  DANGER,
  date,
  dateTime,
  Empty,
  Field,
  MUTED,
  NO_DATA,
  OK,
  Panel,
  PillButton,
  Table,
  WARN,
  WHITE,
} from './dataUi';
import { AirframeForm, emptyInput } from './IdentifyDialog';
import { useIdentifyStore } from './identifyStore';

const STATUS_TONE: Record<InventoryStatus, string> = {
  serviceable: OK,
  maintenance: WARN,
  grounded: DANGER,
  unknown: MUTED,
};

function groupByKind(items: InventoryItem[]): Array<[string, Array<[string, InventoryItem[]]>]> {
  const kinds = new Map<string, Map<string, InventoryItem[]>>();
  for (const item of items) {
    const models = kinds.get(item.kind) ?? new Map<string, InventoryItem[]>();
    const model = item.platform ?? 'Model not recorded';
    models.set(model, [...(models.get(model) ?? []), item]);
    kinds.set(item.kind, models);
  }
  return [...kinds.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([kind, models]) => [kind, [...models.entries()].sort(([a], [b]) => a.localeCompare(b))]);
}

function AirframeRow({
  item,
  selected,
  onSelect,
}: {
  item: InventoryItem;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className="flex w-full flex-wrap items-center gap-x-2 gap-y-1 rounded-[10px] px-2 py-1.5 text-left text-[12px] transition-colors"
      style={{
        background: selected ? 'oklch(1 0 0 / 0.08)' : 'transparent',
        opacity: item.retired ? 0.55 : 1,
      }}
    >
      <span className="tnum min-w-0 truncate" style={{ color: WHITE }} title={item.name}>
        {item.name}
      </span>
      {!item.identified ? <Chip tone={WARN}>UNIDENTIFIED</Chip> : null}
      {item.simulated ? <Chip tone={WARN}>SIM</Chip> : null}
      {item.retired ? <Chip tone={MUTED}>RETIRED</Chip> : null}
      <span className="ml-auto flex items-center gap-1.5">
        {item.currentSlot ? (
          <span className="whitespace-nowrap font-mono text-[9.5px]" style={{ color: OK }}>
            {item.currentSlot}
          </span>
        ) : null}
        <Chip tone={STATUS_TONE[item.status]}>{item.status.toUpperCase()}</Chip>
      </span>
    </button>
  );
}

function AirframeDetailView({
  detail,
  canEdit,
  onChanged,
}: {
  detail: AirframeDetail;
  canEdit: boolean;
  onChanged: (next: AirframeDetail | null, note?: string) => void;
}) {
  const a = detail.airframe;
  const [part, setPart] = useState({ category: '', make: '', model: '', serial: '', firmware: '' });
  const [tail, setTail] = useState(a.tailNumber);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => setTail(a.tailNumber), [a.itemId, a.tailNumber]);

  const apply = async (result: Promise<{ ok: boolean; data: AirframeDetail | null; error: { message: string } | null }>) => {
    const out = await result;
    if (out.ok && out.data) onChanged(out.data);
    else setMessage(out.error?.message ?? 'That did not save.');
  };

  const strikeOff = async () => {
    const result = await removeAirframe(a.itemId);
    if (!result.ok) {
      setMessage(result.error.message);
      return;
    }
    onChanged(
      null,
      result.data.outcome === 'retired'
        ? `${a.name} is retired. It held a drone id, so its flights still resolve to it.`
        : `${a.name} is deleted. It never connected, so there was no history to keep.`,
    );
  };

  const facts: Array<[string, string]> = [
    ['MODEL', a.platform ?? NO_DATA],
    ['MANUFACTURER', a.manufacturer || NO_DATA],
    ['BOARD', a.boardUid ?? 'none reported'],
    ['AIRFRAME SERIAL', a.serialNumber || NO_DATA],
    ['ON DRONE ID', a.currentSlot ?? 'not connected'],
    ['FIRST SEEN', date(a.firstSeen)],
    ['LAST SEEN', dateTime(a.lastSeen)],
    ['IDENTIFIED BY', a.identifiedBy ?? NO_DATA],
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="tnum text-[18px] font-semibold" style={{ color: WHITE }}>
          {a.name}
        </span>
        <Chip tone={STATUS_TONE[a.status]}>{a.status.toUpperCase()}</Chip>
        {a.retired ? <Chip tone={MUTED}>RETIRED {date(a.retiredAt)}</Chip> : null}
        {canEdit && !a.retired ? (
          <button
            type="button"
            onClick={() => void strikeOff()}
            className="label-mono ml-auto text-[8.5px]"
            style={{ color: DANGER }}
          >
            STRIKE OFF
          </button>
        ) : null}
      </div>

      <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-[11px] phone:grid-cols-1">
        {facts.map(([label, value]) => (
          <div key={label} className="flex gap-2">
            <span className="label-mono w-[96px] flex-none text-[8px] leading-[16px]" style={{ color: MUTED }}>
              {label}
            </span>
            <span className="min-w-0 truncate font-mono" style={{ color: WHITE }} title={value}>
              {value}
            </span>
          </div>
        ))}
      </div>

      {canEdit && !a.retired ? (
        <div className="flex flex-wrap items-end gap-2">
          <div className="w-[160px]">
            <Field label="TAIL NUMBER" value={tail} onChange={setTail} mono />
          </div>
          <PillButton
            onClick={() => void apply(updateAirframe(a.itemId, { tailNumber: tail.trim() }))}
            disabled={!tail.trim() || tail === a.tailNumber}
          >
            RENAME
          </PillButton>
          <span className="mx-1 h-6 w-px bg-white/10" />
          {INVENTORY_STATUSES.map((s) => (
            <PillButton
              key={s}
              active={a.status === s}
              onClick={() => void apply(updateAirframe(a.itemId, { status: s }))}
            >
              {s.toUpperCase()}
            </PillButton>
          ))}
        </div>
      ) : null}

      {message ? (
        <p className="text-[11px]" role="status" style={{ color: WARN }}>
          {message}
        </p>
      ) : null}

      <div>
        <h3 className="label-mono mb-1.5 text-[8.5px]" style={{ color: MUTED }}>
          HARDWARE
        </h3>
        <Table
          head={['PART', 'MAKE AND MODEL', 'SERIAL', 'FIRMWARE', 'FITTED', '']}
          empty="No parts recorded."
          rows={detail.components.map((c) => [
            <span key="c" style={{ opacity: c.removedAt ? 0.5 : 1 }}>
              {c.category}
              {c.source !== 'manual' ? (
                <span className="label-mono ml-1.5 text-[7.5px]" style={{ color: MUTED }}>
                  REPORTED
                </span>
              ) : null}
            </span>,
            `${c.make} ${c.model}`.trim() || NO_DATA,
            c.serial || NO_DATA,
            c.firmware || NO_DATA,
            c.removedAt ? `removed ${date(c.removedAt)}` : date(c.fittedAt),
            canEdit && !c.removedAt ? (
              <button
                key="r"
                type="button"
                onClick={() => void apply(removeComponent(c.componentId))}
                className="label-mono text-[8px]"
                style={{ color: MUTED }}
              >
                TAKE OFF
              </button>
            ) : (
              ''
            ),
          ])}
        />
        {canEdit && !a.retired ? (
          <div className="mt-2 grid grid-cols-[1fr_1fr_1fr_1fr_auto] items-end gap-1.5 phone:grid-cols-2">
            <Field label="PART" value={part.category} onChange={(v) => setPart({ ...part, category: v })} placeholder="battery" />
            <Field label="MAKE" value={part.make} onChange={(v) => setPart({ ...part, make: v })} />
            <Field label="MODEL" value={part.model} onChange={(v) => setPart({ ...part, model: v })} />
            <Field label="SERIAL" value={part.serial} onChange={(v) => setPart({ ...part, serial: v })} mono />
            <PillButton
              disabled={!part.category.trim()}
              onClick={() => {
                void apply(fitComponent(a.itemId, part));
                setPart({ category: '', make: '', model: '', serial: '', firmware: '' });
              }}
            >
              FIT
            </PillButton>
          </div>
        ) : null}
      </div>

      <div>
        <h3 className="label-mono mb-1.5 text-[8.5px]" style={{ color: MUTED }}>
          DRONE IDS IT HAS HELD
        </h3>
        <Table
          head={['DRONE ID', 'LINK', 'CONNECTED BY', 'FROM', 'UNTIL']}
          empty="Never connected."
          rows={detail.slots.map((s) => [
            s.droneId,
            s.link || NO_DATA,
            s.connectedBy ?? NO_DATA,
            dateTime(s.connectedAt),
            s.disconnectedAt ? dateTime(s.disconnectedAt) : 'now',
          ])}
        />
      </div>

      <div>
        <h3 className="label-mono mb-1.5 text-[8.5px]" style={{ color: MUTED }}>
          CHANGE HISTORY
        </h3>
        <Table
          head={['WHEN', 'WHAT', 'FROM', 'TO', 'BY']}
          empty="No changes recorded."
          rows={detail.revisions.map((r) => [
            dateTime(r.changedAt),
            r.entity === 'airframe' ? r.field : `${r.entity} ${r.field}`,
            r.oldValue ?? NO_DATA,
            r.newValue ?? NO_DATA,
            r.changedBy,
          ])}
        />
      </div>
    </div>
  );
}

const SPEC_FIELDS: Array<[keyof PlatformSpecInput, string]> = [
  ['batteryWh', 'PACK WH'],
  ['reservePct', 'RESERVE %'],
  ['claimedEnduranceMin', 'ENDURANCE MIN'],
  ['claimedRangeKm', 'RANGE KM'],
  ['claimedMaxSpeedMs', 'TOP SPEED M/S'],
  ['claimedCeilingM', 'CEILING M'],
  ['claimedMaxWindMs', 'WIND LIMIT M/S'],
];

function PlatformSpecs({ canEdit }: { canEdit: boolean }) {
  const [platforms, setPlatforms] = useState<Platform[] | null>(null);
  const [draft, setDraft] = useState<Record<string, Record<string, string>>>({});
  const version = useIdentifyStore((state) => state.version);

  useEffect(() => {
    void getPlatforms().then((result) => setPlatforms(result.ok ? result.data.platforms : []));
  }, [version]);

  if (platforms === null) return null;
  return (
    <Panel
      title="MODELS AND THEIR SPEC SHEETS"
      subtitle="What the manufacturer claims, so the Performance tab can hold it against what the aircraft actually do. The pack size also lets range be estimated before many flights are logged."
    >
      {platforms.length === 0 ? (
        <Empty>No models yet. They appear as aircraft are identified or catalogued.</Empty>
      ) : (
        <div className="flex flex-col gap-3">
          {platforms.map((p) => (
            <div key={p.platformId} className="flex flex-col gap-1.5">
              <div className="flex items-baseline gap-2 text-[12px]">
                <span style={{ color: WHITE }}>{p.name}</span>
                <span style={{ color: MUTED }}>{p.manufacturer}</span>
                <span className="label-mono ml-auto text-[8px]" style={{ color: MUTED }}>
                  {p.airframes ?? 0} ON THE BOOKS
                </span>
              </div>
              <div className="grid grid-cols-7 gap-1.5 tablet:grid-cols-4 phone:grid-cols-2">
                {SPEC_FIELDS.map(([key, label]) => {
                  const stored = p[key];
                  const value = draft[p.platformId]?.[key] ?? (stored === null || stored === undefined ? '' : String(stored));
                  return canEdit ? (
                    <Field
                      key={key}
                      label={label}
                      type="number"
                      value={value}
                      onChange={(v) =>
                        setDraft({ ...draft, [p.platformId]: { ...draft[p.platformId], [key]: v } })
                      }
                    />
                  ) : (
                    <div key={key} className="flex flex-col gap-1">
                      <span className="label-mono text-[8px]" style={{ color: MUTED }}>
                        {label}
                      </span>
                      <span className="tnum text-[12px]">{value || NO_DATA}</span>
                    </div>
                  );
                })}
              </div>
              {canEdit && draft[p.platformId] ? (
                <div>
                  <PillButton
                    onClick={() => {
                      const spec: PlatformSpecInput = {};
                      for (const [key, raw] of Object.entries(draft[p.platformId] ?? {})) {
                        const n = Number(raw);
                        if (raw.trim() !== '' && Number.isFinite(n) && n > 0) {
                          spec[key as keyof PlatformSpecInput] = n;
                        }
                      }
                      void savePlatformSpec(p.platformId, spec).then((result) => {
                        if (result.ok) {
                          setPlatforms(result.data.platforms);
                          const rest = { ...draft };
                          delete rest[p.platformId];
                          setDraft(rest);
                        }
                      });
                    }}
                  >
                    SAVE SPEC
                  </PillButton>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

export function RegisterPanel() {
  const canEdit = useHasRole('operator');
  const pending = useIdentifyStore((state) => state.pending);
  const version = useIdentifyStore((state) => state.version);
  const { open, changed } = useIdentifyStore.getState();
  const [items, setItems] = useState<InventoryItem[] | null>(null);
  const [showRetired, setShowRetired] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<AirframeDetail | null>(null);
  const [cataloguing, setCataloguing] = useState(false);
  const [draft, setDraft] = useState<IdentifyInput>(emptyInput());
  const [note, setNote] = useState<string | null>(null);

  const load = useCallback(() => {
    void getInventory(showRetired).then((result) => {
      if (result.ok) setItems(result.data.items);
    });
  }, [showRetired]);

  useEffect(load, [load, version]);

  useEffect(() => {
    if (!selected) {
      setDetail(null);
      return;
    }
    void getAirframe(selected).then((result) => setDetail(result.ok ? result.data : null));
  }, [selected, version]);

  const groups = useMemo(() => groupByKind(items ?? []), [items]);

  const catalogue = async () => {
    if (!draft.tailNumber.trim()) {
      setNote('A catalogued aircraft needs a tail number.');
      return;
    }
    const result = await catalogueAirframe(draft);
    if (!result.ok) {
      setNote(result.error.message);
      return;
    }
    setCataloguing(false);
    setDraft(emptyInput());
    setSelected(result.data.airframe.itemId);
    setNote(null);
    changed();
  };

  return (
    <div className="flex flex-col gap-3">
      {pending.length > 0 ? (
        <Panel
          title="CONNECTED AND UNIDENTIFIED"
          subtitle="Aircraft on a live drone id that nobody has described yet. The person who connected each one is asked; anyone on the team can answer."
          tone={WARN}
        >
          <div className="flex flex-wrap gap-1.5">
            {pending.map((p) => (
              <button
                key={p.droneId}
                type="button"
                onClick={() => open(p.droneId)}
                className="well flex items-center gap-2 rounded-[10px] px-3 py-1.5 text-[11.5px]"
              >
                <span className="font-mono" style={{ color: WHITE }}>
                  {p.droneId}
                </span>
                <span style={{ color: MUTED }}>
                  {p.board?.uid ? `board ${p.board.uid.slice(-6)}` : 'no board serial'}
                </span>
                {p.connectedBy ? (
                  <span className="label-mono text-[8px]" style={{ color: MUTED }}>
                    BY {p.connectedBy.toUpperCase()}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        </Panel>
      ) : null}

      <div className="grid gap-3" style={{ gridTemplateColumns: 'minmax(280px, 0.8fr) 1.2fr' }}>
        <Panel
          title="AIRFRAMES"
          right={
            <span className="ml-auto flex items-center gap-1.5">
              <PillButton active={showRetired} onClick={() => setShowRetired((v) => !v)}>
                RETIRED
              </PillButton>
              {canEdit ? (
                <PillButton active={cataloguing} onClick={() => setCataloguing((v) => !v)}>
                  CATALOGUE
                </PillButton>
              ) : null}
            </span>
          }
        >
          {items === null ? null : items.length === 0 ? (
            <Empty>Nothing on the register yet. Aircraft appear here as they connect.</Empty>
          ) : (
            <div className="flex flex-col gap-3">
              {groups.map(([kind, models]) => (
                <div key={kind} className="flex flex-col gap-1">
                  <span className="label-mono text-[8.5px]" style={{ color: MUTED, letterSpacing: '0.14em' }}>
                    {kind.toUpperCase()}
                  </span>
                  {models.map(([model, rows]) => (
                    <div key={model} className="flex flex-col pl-2">
                      <span className="text-[10.5px]" style={{ color: MUTED }}>
                        {model}
                      </span>
                      {rows.map((item) => (
                        <AirframeRow
                          key={item.itemId}
                          item={item}
                          selected={selected === item.itemId}
                          onSelect={() => {
                            setSelected(item.itemId);
                            setCataloguing(false);
                          }}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel
          title={cataloguing ? 'CATALOGUE AN AIRCRAFT' : 'AIRFRAME'}
          subtitle={
            cataloguing
              ? 'For an aircraft that has not connected yet. When it does, pick it from the prompt and its board is bound to this record.'
              : undefined
          }
        >
          {note ? (
            <p className="mb-3 text-[11px]" role="status" style={{ color: WARN }}>
              {note}
            </p>
          ) : null}
          {cataloguing ? (
            <div className="flex flex-col gap-3">
              <AirframeForm value={draft} onChange={setDraft} />
              <div>
                <PillButton onClick={() => void catalogue()}>ADD TO THE REGISTER</PillButton>
              </div>
            </div>
          ) : detail ? (
            <AirframeDetailView
              detail={detail}
              canEdit={canEdit}
              onChanged={(next, message) => {
                setNote(message ?? null);
                if (next) setDetail(next);
                else setSelected(null);
                changed();
              }}
            />
          ) : (
            <Empty>Pick an airframe to see its hardware, the drone ids it has held and its history.</Empty>
          )}
        </Panel>
      </div>

      <PlatformSpecs canEdit={canEdit} />
    </div>
  );
}
